import { AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { APIToUser, setUserInfo } from '../users';
import { getRequestErrorType } from '../utilities/API';
import { HEADER_SLUG_PROP } from '^/constants/network';
import { jsonContentHeader, makeV2APIURL } from '^/store/duck/API';
import { ChangeAuthedUser } from '^/store/duck/Auth';
import { ChangeLanguage, ShowSignUpTutorialPopup } from '^/store/duck/Pages';
import { insertUserToSentry } from '^/store/duck/vendor/sentry';
import * as T from '^/types';
import route from '^/constants/routes';
import { AuthKeys, AUTH_KEYS, getPlanURL } from './constants';
import { queryClient } from '^/store/react-query';
import { belongsToPlan } from '^/utilities/plans';
import {
  cancelSignIn,
  cancelSignUp,
  finishSignIn,
  finishSignUp,
  startSignIn,
  startSignUp,
} from '^/store/auth';
import { useCallback } from 'react';
import jwtDecode from 'jwt-decode';
import { getAuthedUserSerializer } from '^/utilities/serializers-utils';
import { http } from '^/utilities/api';
import { useNavigate } from 'react-router-dom';
import { SESSION_LOGOUT_TOAST_ID } from '^/hooks/useSessionLogout';
import { toast } from 'react-toastify';
import { UserKeys } from '../users/constants';

type PostSignInBody = Pick<T.User, 'email'> &
  Pick<T.UserPassword, 'password'> &
  Pick<T.AutoSignIn, 'autoSignIn'>;
type PostSignUpBody = Readonly<
  Pick<T.User, Exclude<T.SignUpFormUserKeys, 'avatar'>> & Pick<T.UserPassword, 'password'>
> & { avatar?: File };

export interface GetSignInResponse {
  readonly data: T.APIAuthUser;
  readonly meta: T.APICDNCookie;
}

export interface GetSignUpResponse {
  readonly data: T.APIAuthUser;
}

export const getAuthedUser = () =>
  queryClient.getQueryData<T.AuthState['authedUser']>(AuthKeys.getAuthedUserKey);
export const setAuthedUser = (authedUser?: T.AuthState['authedUser']) =>
  queryClient.setQueryData<T.AuthState['authedUser']>(AuthKeys.getAuthedUserKey, authedUser);

export const getLanguage = () => queryClient.getQueryData<T.Language>(AuthKeys.getLanguageKey);
export const setLanguage = (language: T.Language) =>
  queryClient.setQueryData<T.Language>(AuthKeys.getLanguageKey, language);

const postSignInFunction =
  (slug?: string) =>
  async ({ email, password, autoSignIn }: PostSignInBody) => {
    const URL: string = getPlanURL(slug);
    const headers = {
      ...jsonContentHeader,
      [HEADER_SLUG_PROP]: slug,
    };
    const signInPayload = {
      email,
      password,
      remember: autoSignIn,
    };
    const response = await http.post<GetSignInResponse>(URL, signInPayload, { headers });
    return response.data;
  };

// For initial login
export const useSignInQuery = () => {
  const dispatch = useDispatch();
  const slug: string | undefined = useSelector((s: T.State) => s.PlanConfig.config?.slug);
  const fallbackLang: T.Language = useSelector((s: T.State) => s.Pages.Common.language);
  const mutattionFn = useCallback(postSignInFunction(slug), [slug]);

  return useMutation(mutattionFn, {
    onMutate() {
      startSignIn();
    },
    onSuccess(result) {
      const { data } = result;
      const user = APIToUser(data, slug);
      setUserInfo(user);
      const authedUser = getAuthedUserSerializer(result) satisfies T.AuthState['authedUser'];
      dispatch(ChangeAuthedUser({ authedUser }));
      toast.dismiss(SESSION_LOGOUT_TOAST_ID);
      queryClient.setQueryData(UserKeys.getUserInfoKey(authedUser.id), user);
      dispatch(
        ChangeLanguage({
          language: user.language ?? fallbackLang,
        })
      );
      // setLanguage(user.language ?? fallbackLang);
      insertUserToSentry((({ id, ...others }) => ({ id: String(id), ...others }))({ ...user }));
      finishSignIn({});
      dispatch(ShowSignUpTutorialPopup());
    },
    onError(err: AxiosError) {
      cancelSignIn();
      finishSignIn({
        error: getRequestErrorType(err),
        errorCode: err.response?.status,
      });
    },
  });
};

const postSignUpFunction = (slug?: string) => async (formValues: PostSignUpBody) => {
  const URL: string = belongsToPlan(slug)
    ? makeV2APIURL('plans', slug ?? '', 'signup')
    : makeV2APIURL('auth', 'signup');
  const headers = {
    ...jsonContentHeader,
    [HEADER_SLUG_PROP]: slug,
  };

  const formData: FormData = new FormData();
  for (const key of Object.keys(formValues)) {
    const value: string | File | undefined = formValues[key as keyof PostSignUpBody];
    if (value) {
      formData.append(key, value);
    }
  }

  const response = await http.post<GetSignUpResponse>(URL, formData, { headers });
  return response.data;
};

export const useSignUpMutation = () => {
  const slug: string | undefined = useSelector((s: T.State) => s.PlanConfig.config?.slug);
  const client = useQueryClient();
  const mutationFn = useCallback(postSignUpFunction(slug), [slug]);
  const navigate = useNavigate();

  return useMutation(mutationFn, {
    onMutate() {
      startSignUp();
    },
    onSuccess(result) {
      const { data } = result;
      const user = APIToUser(data, slug);
      setUserInfo(user);
      finishSignUp({});
      navigate(`${route.signup.main}?email=${encodeURIComponent(data.attributes.email)}`);
    },
    onError(err: AxiosError) {
      cancelSignUp();
      finishSignUp({
        error: getRequestErrorType(err),
      });
    },
    async onSettled() {
      await client.invalidateQueries([AUTH_KEYS.CheckEmail]);
    },
  });
};

interface CheckEmailResponse {
  status: 0 | 1;
  description: string;
  emailConfirmed: boolean;
  approved: boolean;
}
interface useCheckEmailQueryProps {
  email?: string;
}
export const useCheckEmailQuery = ({ email }: useCheckEmailQueryProps) =>
  useQuery(
    AuthKeys.checkEmailKey(email),
    async () => {
      const url: string = makeV2APIURL(
        `check_email_exists?email=${encodeURIComponent(email || '')}`
      );
      return (await http.get<CheckEmailResponse>(url)).data;
    },
    {
      enabled: Boolean(email),
    }
  );

export const useSAMLSSOMutation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug, fallbackLang } = useSelector((s: T.State) => ({
    slug: s.PlanConfig.config?.slug,
    fallbackLang: s.Pages.Common.language,
  }));

  const mutationFn = async ({ token }: { token: string }) => {
    const url = makeV2APIURL('auth', 'saml', 'signin');
    return (
      await http.post(url, null, {
        headers: { Authorization: token },
      })
    ).data;
  };

  return useMutation(mutationFn, {
    onSuccess({ data, meta }: GetSignInResponse, { token }) {
      const { email } = jwtDecode<{ email: string }>(token);
      if (!data || !meta) {
        navigate(`${route.signup.main}?email=${encodeURIComponent(email)}`);
        return;
      }
      const user = APIToUser(data, slug);
      setUserInfo(user);
      const authedUser = {
        id: parseInt(data.id, 10),
        token: data.attributes.token,
        cloudFrontPolicy: meta?.cloudFrontPolicy,
        cloudFrontSignature: meta?.cloudFrontSignature,
        cloudFrontKeyPairId: meta?.cloudFrontKeyPairId,
        cloudCdnCookie: meta?.cloudCdnCookie,
        refreshToken: data?.attributes.refreshToken,
        userEmail: data?.attributes.email,
      } satisfies T.AuthState['authedUser'];
      dispatch(ChangeAuthedUser({ authedUser }));
      dispatch(
        ChangeLanguage({
          language: user.language ?? fallbackLang,
        })
      );
      insertUserToSentry((({ id, ...others }) => ({ id: String(id), ...others }))({ ...user }));
      finishSignIn({});
      dispatch(ShowSignUpTutorialPopup());
    },
    onError(err: AxiosError, { token }) {
      if (err.response?.status === 401) {
        const { email } = jwtDecode<{ email: string }>(token);
        navigate(`${route.signup.main}?email=${encodeURIComponent(email)}`);
      }
      cancelSignIn();
      finishSignIn({
        error: getRequestErrorType(err),
        errorCode: err.response?.status,
      });
    },
  });
};
